import { FC } from "react";
import classNames from "classnames";
import {
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import OverviewScatterChartTooltip from "./OverviewScatterChartTooltip/OverviewScatterChartTooltip";

import { OverviewScatterChartProps, ScatterLogoShapeProps } from "./types";
import {
  EyeIcon,
  SentimentPositiveIcon,
} from "../icons";
import { getInitials } from "./utils/getInitials";
import Skeleton from "../Skeleton/Skeleton";

import styles from "./OverviewScatterChart.module.css";



function ScatterLogoShape({ cx, cy, payload }: ScatterLogoShapeProps) {
  if (cx == null || cy == null || !payload) return null;

  const size = 48;
  const x = cx - size / 2;
  const y = cy - size / 2;
  const clipId = `scatter-logo-clip-${payload.id}`;
  const hasLogo = Boolean(payload.logoUrl?.trim());

  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect x={x} y={y} width={size} height={size} rx={8} />
        </clipPath>
      </defs>
      <rect
        x={x}
        y={y}
        width={size}
        height={size}
        rx={8}
        fill="var(--aicarma-color-white)"
        style={{ filter: "drop-shadow(0px 12px 18px rgba(0, 54, 78, 0.18))" }}
      />
      {hasLogo ? (
        <g clipPath={`url(#${clipId})`}>
          <image
            href={payload.logoUrl}
            x={x}
            y={y}
            width={size}
            height={size}
            preserveAspectRatio="xMidYMid slice"
          />
        </g>
      ) : (
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={14}
          fontWeight={700}
          fill="var(--aicarma-color-dark2)"
        >
          {getInitials(payload.label)}
        </text>
      )}
    </g>
  );
}

const EMPTY_MESSAGE = "No data for the selected period";

const OverviewScatterChart: FC<OverviewScatterChartProps> = ({
  className,
  scatterChartData,
  isLoading = false,
}) => {
  if (isLoading && scatterChartData == null) {
    return <Skeleton height={340} variant="text" />;
  }

  if (
    !isLoading &&
    (scatterChartData == null || scatterChartData.data.length === 0)
  ) {
    return (
      <div className={styles["overview-scatter-chart__empty"]}>
        <p>{EMPTY_MESSAGE}</p>
      </div>
    );
  }

  const { data, averageVisibility } = scatterChartData!;

  const PADDING = 5;
  const MIN_DOMAIN = 0;
  const MAX_DOMAIN = 100;

  const visValues = data.map((d) => d.visibility);
  const sentValues = data.map((d) => d.sentiment);
  const minVis = Math.min(...visValues);
  const maxVis = Math.max(...visValues);
  const minSent = Math.min(...sentValues);
  const maxSent = Math.max(...sentValues);

  const domainY: [number, number] = (() => {
    const low = Math.max(MIN_DOMAIN, minVis - PADDING);
    const high = Math.min(MAX_DOMAIN, maxVis + PADDING);
    return low < high ? [low, high] : [Math.max(0, low - PADDING), Math.min(100, high + PADDING)];
  })();
  const domainX: [number, number] = (() => {
    const low = Math.max(MIN_DOMAIN, minSent - PADDING);
    const high = Math.min(MAX_DOMAIN, maxSent + PADDING);
    return low < high ? [low, high] : [Math.max(0, low - PADDING), Math.min(100, high + PADDING)];
  })();

  return (
    <div className={classNames(styles["overview-scatter-chart"], className)}>
      <div className={styles["overview-scatter-chart__body"]}>
        <div
          className={classNames(
            styles["overview-scatter-chart__axis-label"],
            styles["is-y"]
          )}
        >
          <EyeIcon className={styles["overview-scatter-chart__axis-icon"]} />
          VISIBILITY
        </div>

        <div
          className={classNames(
            styles["overview-scatter-chart__axis-label"],
            styles["is-x"]
          )}
        >
          <SentimentPositiveIcon
            className={styles["overview-scatter-chart__axis-icon"]}
          />
          SENTIMENT
        </div>

        <div
          className={classNames(
            styles["overview-scatter-chart__quadrant"],
            styles["is-top-left"]
          )}
        >
          LOW PERFORMANCE
        </div>
        <div
          className={classNames(
            styles["overview-scatter-chart__quadrant"],
            styles["is-top-right"]
          )}
        >
          LEADERS
        </div>
        <div
          className={classNames(
            styles["overview-scatter-chart__quadrant"],
            styles["is-bottom-left"]
          )}
        >
          LOW CONVERSION
        </div>
        <div
          className={classNames(
            styles["overview-scatter-chart__quadrant"],
            styles["is-bottom-right"]
          )}
        >
          NICHE
        </div>

        <div className={styles["overview-scatter-chart__chart"]}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ left: 14, bottom: 28 }}>
              <ReferenceLine
                x={50}
                stroke="var(--aicarma-color-gray2)"
                strokeDasharray="4 4"
              />
              <ReferenceLine
                y={50}
                stroke="var(--aicarma-color-gray2)"
                strokeDasharray="4 4"
              />

              <ReferenceArea
                y1={averageVisibility}
                y2={domainY[0]}
                fill="url(#area-gradient-green)"
                fillOpacity={1}
                stroke="none"
              />

              <XAxis
                type="number"
                dataKey="sentiment"
                tickLine={false}
                axisLine={{ stroke: "var(--aicarma-color-gray2)" }}
                domain={domainX}
                tickCount={6}
                allowDataOverflow
              />
              <YAxis
                type="number"
                dataKey="visibility"
                tickLine={false}
                axisLine={{ stroke: "var(--aicarma-color-gray2)" }}
                domain={domainY}
                tickCount={6}
                allowDataOverflow
              />

              <Tooltip content={<OverviewScatterChartTooltip />} cursor={false} />

              <Scatter data={data} shape={<ScatterLogoShape />} />

              <defs>
                <linearGradient
                  id="area-gradient-green"
                  gradientUnits="objectBoundingBox"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0" stopColor="#DCF3E0" />
                  <stop offset="1" stopColor="#FFFFFF" />
                </linearGradient>
              </defs>

            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default OverviewScatterChart;
